import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type {
  ESSReferenceProject,
  ESSSectorBenchmark,
  ESSFinancialSnapshot,
  ProjectSimilarity,
  BenchmarkComparison,
  ESSProjectData,
  SectorMetrics
} from '../types/essReference';
import type { FinancialObjectives } from '../types/businessPlan';

interface ESSReferenceState {
  referenceProjects: ESSReferenceProject[];
  sectorBenchmarks: ESSSectorBenchmark[];
  similarProjects: ProjectSimilarity[];
  currentBenchmark: ESSSectorBenchmark | null;
  isLoading: boolean;
  error: string | null;

  loadReferenceProjects: () => Promise<void>;
  loadSectorBenchmarks: () => Promise<void>;
  getBenchmarkByCategory: (category: string) => Promise<ESSSectorBenchmark | null>;
  findSimilarProjects: (category: string, objectives: FinancialObjectives) => Promise<ProjectSimilarity[]>;
  compareWithSector: (category: string, objectives: FinancialObjectives) => Promise<BenchmarkComparison[]>;
  importProjectFromJSON: (jsonData: ESSProjectData) => Promise<void>;
  refreshBenchmarks: () => Promise<void>;
  clearError: () => void;
}

export const useESSReferenceStore = create<ESSReferenceState>((set, get) => ({
  referenceProjects: [],
  sectorBenchmarks: [],
  similarProjects: [],
  currentBenchmark: null,
  isLoading: false,
  error: null,

  loadReferenceProjects: async () => {
    set({ isLoading: true, error: null });

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase non configuré');
      }

      const { data, error } = await supabase
        .from('ess_reference_projects')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;

      set({ referenceProjects: data || [], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  loadSectorBenchmarks: async () => {
    set({ isLoading: true, error: null });

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase non configuré');
      }

      const { data, error } = await supabase
        .from('ess_sector_benchmarks')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;

      set({ sectorBenchmarks: data || [], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  getBenchmarkByCategory: async (category: string) => {
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase non configuré');
      }

      const { data, error } = await supabase
        .from('ess_sector_benchmarks')
        .select('*')
        .eq('category', category)
        .maybeSingle();

      if (error) throw error;

      set({ currentBenchmark: data });
      return data;
    } catch (error: any) {
      set({ error: error.message });
      return null;
    }
  },

  findSimilarProjects: async (category: string, objectives: FinancialObjectives) => {
    set({ isLoading: true, error: null });

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase non configuré');
      }

      const { data: projects, error } = await supabase
        .from('ess_reference_projects')
        .select('*')
        .eq('category', category);

      if (error) throw error;

      if (!projects || projects.length === 0) {
        set({ similarProjects: [], isLoading: false });
        return [];
      }

      const similarities: ProjectSimilarity[] = projects.map(project => {
        const firstYearKey = Object.keys(project.data.finance.pnl)[0];
        const firstYearPNL = project.data.finance.pnl[firstYearKey];

        let score = 0;
        const factors: string[] = [];

        if (firstYearPNL) {
          const revenueMatch = Math.abs(firstYearPNL.revenue_total_eur - objectives.ca_target) / objectives.ca_target;
          if (revenueMatch < 0.3) {
            score += 40;
            factors.push('CA similaire');
          } else if (revenueMatch < 0.5) {
            score += 20;
          }

          const margeMatch = Math.abs(firstYearPNL.ebitda_margin_pct - objectives.marge_target) / objectives.marge_target;
          if (margeMatch < 0.3) {
            score += 30;
            factors.push('Marge similaire');
          } else if (margeMatch < 0.5) {
            score += 15;
          }
        }

        if (project.data.finance.capex) {
          const capexMatch = Math.abs(project.data.finance.capex.total_eur - objectives.investment_budget) / objectives.investment_budget;
          if (capexMatch < 0.3) {
            score += 30;
            factors.push('Investissement similaire');
          } else if (capexMatch < 0.5) {
            score += 15;
          }
        }

        if (category === project.category) {
          factors.push('Même secteur');
        }

        return {
          project,
          similarity_score: score,
          matching_factors: factors
        };
      });

      similarities.sort((a, b) => b.similarity_score - a.similarity_score);
      const topSimilar = similarities.slice(0, 5);

      set({ similarProjects: topSimilar, isLoading: false });
      return topSimilar;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      return [];
    }
  },

  compareWithSector: async (category: string, objectives: FinancialObjectives) => {
    try {
      const benchmark = await get().getBenchmarkByCategory(category);

      if (!benchmark || !benchmark.metrics) {
        return [];
      }

      const metrics = benchmark.metrics as SectorMetrics;
      const comparisons: BenchmarkComparison[] = [];

      if (metrics.avg_revenue_year1) {
        const deviation = ((objectives.ca_target - metrics.avg_revenue_year1) / metrics.avg_revenue_year1) * 100;
        comparisons.push({
          metric: 'Chiffre d\'Affaires Cible',
          user_value: objectives.ca_target,
          sector_avg: metrics.avg_revenue_year1,
          sector_min: metrics.min_revenue || 0,
          sector_max: metrics.max_revenue || 0,
          position: Math.abs(deviation) < 20 ? 'average' : (deviation > 0 ? 'above' : 'below'),
          deviation_pct: deviation
        });
      }

      if (metrics.avg_ebitda_margin_pct) {
        const deviation = ((objectives.marge_target - metrics.avg_ebitda_margin_pct) / metrics.avg_ebitda_margin_pct) * 100;
        comparisons.push({
          metric: 'Marge Nette Cible (%)',
          user_value: objectives.marge_target,
          sector_avg: metrics.avg_ebitda_margin_pct,
          sector_min: metrics.min_ebitda_margin_pct || 0,
          sector_max: metrics.max_ebitda_margin_pct || 0,
          position: Math.abs(deviation) < 20 ? 'average' : (deviation > 0 ? 'above' : 'below'),
          deviation_pct: deviation
        });
      }

      if (metrics.avg_capex) {
        const deviation = ((objectives.investment_budget - metrics.avg_capex) / metrics.avg_capex) * 100;
        comparisons.push({
          metric: 'Budget d\'Investissement',
          user_value: objectives.investment_budget,
          sector_avg: metrics.avg_capex,
          sector_min: 0,
          sector_max: 0,
          position: Math.abs(deviation) < 20 ? 'average' : (deviation > 0 ? 'above' : 'below'),
          deviation_pct: deviation
        });
      }

      return comparisons;
    } catch (error: any) {
      set({ error: error.message });
      return [];
    }
  },

  importProjectFromJSON: async (jsonData: ESSProjectData) => {
    set({ isLoading: true, error: null });

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase non configuré');
      }

      const { data: existingProject } = await supabase
        .from('ess_reference_projects')
        .select('id')
        .eq('project_id', jsonData.meta.project_id)
        .maybeSingle();

      const projectData = {
        project_id: jsonData.meta.project_id,
        project_name: jsonData.meta.project_name,
        category: jsonData.meta.category,
        territory: jsonData.meta.territory,
        legal_form: jsonData.governance.legal_form,
        data: jsonData
      };

      let projectId: string;

      if (existingProject) {
        const { data, error } = await supabase
          .from('ess_reference_projects')
          .update(projectData)
          .eq('id', existingProject.id)
          .select()
          .single();

        if (error) throw error;
        projectId = data.id;
      } else {
        const { data, error } = await supabase
          .from('ess_reference_projects')
          .insert(projectData)
          .select()
          .single();

        if (error) throw error;
        projectId = data.id;
      }

      const snapshots = [];
      for (const [yearKey, pnlData] of Object.entries(jsonData.finance.pnl)) {
        const year = parseInt(yearKey);
        const impactData = jsonData.impact.years[yearKey];

        snapshots.push({
          reference_project_id: projectId,
          year,
          revenue_total: pnlData.revenue_total_eur,
          ebitda: pnlData.ebitda_eur,
          ebitda_margin_pct: pnlData.ebitda_margin_pct,
          net_income: pnlData.net_income_eur,
          jobs_created: impactData?.jobs_created || 0,
          capex_total: year === parseInt(Object.keys(jsonData.finance.pnl)[0])
            ? jsonData.finance.capex.total_eur
            : 0
        });
      }

      if (snapshots.length > 0) {
        const { error: snapshotError } = await supabase
          .from('ess_financial_snapshots')
          .upsert(snapshots, {
            onConflict: 'reference_project_id,year'
          });

        if (snapshotError) throw snapshotError;
      }

      await get().loadReferenceProjects();
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  refreshBenchmarks: async () => {
    set({ isLoading: true, error: null });

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase non configuré');
      }

      const { error } = await supabase.rpc('calculate_sector_benchmarks');

      if (error) throw error;

      await get().loadSectorBenchmarks();
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null })
}));
